import React from "react"

import { useAgentsList } from "@/agents/catalog"
import { useHumanReviewProjectsTransport } from "@/human-feedback/transport"

import { formatMetadataFilter } from "@/lib/metadataFilters"
import {
  ActiveFilterBar,
  AddFilterButton,
} from "@/components/filters/ActiveFilterBar"
import { EditableFilterChip } from "@/components/filters/EditableFilterChip"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"
import { formatDateRangeLabel } from "@/utils/date/dateRangeLabel"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import { useConversationsPopover } from "../contexts/ConversationsPopoverContext"
import { useAllMetrics } from "../hooks/useAllMetrics"
import { ConversationsFilterDropdown } from "./filters"

export const ConversationsFilterChips: React.FC<{
  clusterFilterCount?: number | null
  filterEnabled?: boolean
  onClearClusterFilter?: () => void
  /** Rows behind the metric panel of the menu this bar's `+` opens. */
  conversations?: ConversationsTableResult[]
}> = ({
  clusterFilterCount = null,
  filterEnabled = true,
  onClearClusterFilter,
  conversations = [],
}) => {
  const {
    state: {
      metricIds,
      agentIds,
      metadataFilters,
      metricValueFilters,
      dateRange,
      tagFilters,
      projectIds,
    },
    setMetricIds,
    setAgentIds,
    setTagFilters,
    setProjectIds,
    handlerRemoveMetadataFilter,
    setMetricCondition,
    handlerClearDateRange,
    hasActiveFilters,
    clearAllFilters,
  } = useConversationsFilter()

  const { onOpenChange, setFilterSection } = useConversationsPopover("filters")

  // Fetch data for display names
  const { data: allMetrics } = useAllMetrics({ enabled: filterEnabled })
  const { data: agents } = useAgentsList({ enabled: filterEnabled })
  const { data: projects } = useHumanReviewProjectsTransport({
    enabled: filterEnabled && projectIds.length > 0,
  })

  const hasClusterFilter =
    clusterFilterCount !== null && Boolean(onClearClusterFilter)

  if (!hasActiveFilters && !hasClusterFilter) {
    return null
  }

  const chips: Array<{
    id: string
    label: string
    property?: string
    operator?: string | null
    onRemove: () => void
  }> = []

  // Visualizer cluster filter chip -- the only indication a click-to-filter-by-cluster
  // is active, and the only discoverable way to clear it (source: not in
  // ConversationsFilterContext, so it can't participate in "Clear all").
  if (hasClusterFilter && onClearClusterFilter) {
    chips.push({
      id: "cluster-filter",
      label: `${clusterFilterCount} conversation${clusterFilterCount === 1 ? "" : "s"}`,
      onRemove: onClearClusterFilter,
    })
  }

  // Date range chip
  const dateLabel = formatDateRangeLabel(dateRange, { style: "connector" })
  if (dateLabel) {
    chips.push({
      id: "date-range",
      label: dateLabel,
      onRemove: handlerClearDateRange,
    })
  }

  metricIds.forEach((metricId) => {
    chips.push({
      id: `metric-${metricId}`,
      property: "Has Metric",
      operator: null,
      label: resolveMetricDisplayName(
        Object.values(allMetrics ?? {}).find((metric) => metric.id === metricId)
          ?.metric_display_name,
        metricId,
      ),
      onRemove: () => {
        // An "Any value" condition on the same metric has no chip of its own
        // (see below), so removing this chip clears it too.
        if (
          metricValueFilters.some(
            (filter) =>
              filter.metricId === metricId && filter.operator === "exists",
          )
        ) {
          setMetricCondition(metricId, undefined)
          return
        }
        setMetricIds(metricIds.filter((id) => id !== metricId))
      },
    })
  })
  agentIds.forEach((agentId) => {
    chips.push({
      id: `agent-${agentId}`,
      label:
        agents?.find((agent) => agent.id === agentId)?.display_name || agentId,
      onRemove: () => setAgentIds(agentIds.filter((id) => id !== agentId)),
    })
  })
  tagFilters.forEach((tag) => {
    chips.push({
      id: `tag-${tag}`,
      label: tag,
      onRemove: () =>
        setTagFilters(tagFilters.filter((value) => value !== tag)),
    })
  })
  projectIds.forEach((projectId) => {
    chips.push({
      id: `project-${projectId}`,
      label:
        projects?.find((project) => project.ulid === projectId)?.name ||
        projectId,
      onRemove: () =>
        setProjectIds(projectIds.filter((id) => id !== projectId)),
    })
  })

  // Metric value filter chips
  metricValueFilters.forEach((filter) => {
    if (filter.operator === "exists" && metricIds.includes(filter.metricId)) {
      return
    }
    chips.push({
      id: `metric-value-${filter.metricId}`,
      property: filter.operator === "exists" ? "Has Metric" : "Metric",
      operator: null,
      label:
        filter.operator === "exists"
          ? filter.metricDisplayName
          : `${filter.metricDisplayName} ${filter.operator} ${filter.value === "" ? "(empty)" : filter.value}`,
      onRemove: () => setMetricCondition(filter.metricId, undefined),
    })
  })

  // Metadata chips
  metadataFilters.forEach((filter, index) => {
    chips.push({
      id: `metadata-${index}`,
      label: formatMetadataFilter(filter),
      onRemove: () => handlerRemoveMetadataFilter(index),
    })
  })

  return (
    <ActiveFilterBar
      onClear={() => {
        clearAllFilters()
        onClearClusterFilter?.()
      }}
      addControl={
        <ConversationsFilterDropdown
          conversations={conversations}
          filterEnabled={filterEnabled}
          trigger={<AddFilterButton />}
        />
      }
    >
      {chips.map((chip) => {
        if (chip.id === "cluster-filter") {
          return (
            <EditableFilterChip
              key={chip.id}
              property="Cluster"
              value={chip.label}
              onRemove={chip.onRemove}
            />
          )
        }
        if (chip.id === "date-range") {
          return (
            <EditableFilterChip
              key={chip.id}
              property="Conversation Time"
              value={chip.label}
              onRemove={chip.onRemove}
              onEdit={() => {
                setFilterSection("date")
                onOpenChange(true)
              }}
            />
          )
        }
        const section = chip.id.startsWith("metric")
          ? "metrics"
          : chip.id.startsWith("metadata")
            ? "metadata"
            : chip.id.startsWith("agent-")
              ? "agents"
              : chip.id.startsWith("project-")
                ? "projects"
                : "tags"
        const property =
          section === "metrics"
            ? "Metric"
            : section === "metadata"
              ? "Metadata"
              : section === "agents"
                ? "Agent"
                : section === "projects"
                  ? "Project"
                  : "Tag"
        return (
          <EditableFilterChip
            key={chip.id}
            property={chip.property ?? property}
            operator={chip.operator}
            value={chip.label}
            onRemove={chip.onRemove}
            onEdit={() => {
              setFilterSection(section)
              onOpenChange(true)
            }}
          />
        )
      })}
    </ActiveFilterBar>
  )
}
